import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { compact, isArray } from 'underscore';

type ValidatorErrors = string[] | null;
type Validator<T> = (value: T) => string | null;

function validate<T>(value: T, validators: Validator<T> | Validator<T>[]) {
  let validatorsToRun;
  if (!isArray(validators)) {
    validatorsToRun = [validators];
  } else {
    validatorsToRun = validators;
  }

  let errors: ValidatorErrors = compact(
    validatorsToRun.map((validator) => validator(value)),
  );
  if (errors.length === 0) {
    errors = null;
  }

  return errors;
}

export function useValidatedState<T>(
  initialValue: T,
  validators: Validator<T> | Validator<T>[],
): [T, Dispatch<SetStateAction<T>>, ValidatorErrors] {
  const [value, setValue] = useState<T>(initialValue);

  return [value, setValue, validate(value, validators)];
}

export function useValidatedSyncedState<T>(
  sourceValue: T,
  initialValue: T,
  validators: Validator<T> | Validator<T>[],
): [T, Dispatch<SetStateAction<T>>, ValidatorErrors] {
  const [value, setValue] = useState<T>(sourceValue || initialValue);
  useEffect(() => {
    setValue(sourceValue);
  }, [sourceValue]);

  return [value, setValue, validate(value, validators)];
}
